/**
 * Find creators — the brand going looking, instead of waiting to be asked.
 *
 * WHY THIS PANEL EXISTS. The tab next door shows creators who knocked. Until
 * this screen, that was the ONLY way a brand ever acquired a partner: a brand
 * that had just switched its catalog to "approved creators only" and had no
 * applicants yet could do precisely nothing. Recruiting partners is the
 * headline feature of every comparable platform, not a footnote.
 *
 * AN INVITE IS A PRE-APPROVAL, and the copy says so. It puts the creator in
 * exactly the state they would have reached by asking and being approved —
 * there is no "invited, pending acceptance" limbo to explain, and they can
 * start promoting immediately. They are notified in their own app.
 *
 * SAME PROXY, SAME CONSEQUENCES as the requests panel:
 *  - **A failure is not an empty directory.** GNCreators unreachable answers
 *    503, and this renders an error rather than "no creators found", which
 *    would read as "nobody is on this platform".
 *  - **Only what a brand needs to choose is shown** — name, handle, followers,
 *    niches. No email, no phone. Recognising a creator is the job; contacting
 *    them off platform is not, and the API does not send it.
 *
 * ALREADY-APPROVED CREATORS STILL APPEAR, with the button disabled rather than
 * the row hidden. A brand searching for someone it already works with should
 * find them and see that fact, not conclude they are not on the platform.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  palette,
  radius,
  motion,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';

/** 12345 -> "12.3K". Matches the requests panel so one creator reads the same
 *  in both places. */
const compactCount = (n) => {
  const value = Number(n) || 0;
  if (value < 1000) return String(value);
  if (value < 100000) return `${(value / 1000).toFixed(value < 10000 ? 1 : 0)}K`;
  return `${(value / 100000).toFixed(1)}L`;
};

const FOLLOWER_TIERS = [
  { value: 0, label: 'Any size' },
  { value: 1000, label: '1K+' },
  { value: 10000, label: '10K+' },
  { value: 100000, label: '100K+' },
];

/** What the button says, given where this creator already stands with us. */
export const inviteState = (row) => {
  switch (row.access_status) {
    case 'APPROVED':
      return { label: 'Already approved', disabled: true, done: true };
    case 'REQUESTED':
      // They asked first. Inviting still works and simply approves them, so
      // the button stays live — but the label says what is really happening.
      return { label: 'Approve', disabled: false, done: false };
    case 'REJECTED':
    case 'REVOKED':
      return { label: 'Invite again', disabled: false, done: false };
    default:
      return { label: 'Invite', disabled: false, done: false };
  }
};

const CreatorDirectory = ({ onNotify }) => {
  const [rows, setRows] = useState([]);
  const [niches, setNiches] = useState([]);
  const [query, setQuery] = useState('');
  const [niche, setNiche] = useState('');
  const [minFollowers, setMinFollowers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await CommerceService.searchCreators({
        q: query || undefined,
        niche: niche || undefined,
        min_followers: minFollowers || undefined,
      });
      setRows(response.data?.results || []);
    } catch (err) {
      // Deliberately NOT an empty list — see the file header.
      setError(readApiError(err, 'Could not load the creator directory.'));
      setRows([]);
    }
    setLoading(false);
  }, [query, niche, minFollowers]);

  useEffect(() => {
    // Debounced so typing a name is one request at the end, not one per key.
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    CommerceService.creatorFacets()
      .then((r) => setNiches(r.data?.niches || []))
      // A missing filter vocabulary is not worth an error message; the search
      // box still works without it.
      .catch(() => setNiches([]));
  }, []);

  const invite = async (row) => {
    setInviting(row.handler_id);
    try {
      await CommerceService.inviteCreator(row.handler_id);
      onNotify(`${row.display_name} can now promote your products.`, 'success');
      load();
    } catch (err) {
      onNotify(readApiError(err, 'Could not invite that creator.'), 'error');
    }
    setInviting(null);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: palette.ink, mb: 0.5 }}>
        Find creators
      </Typography>
      <Typography sx={{ fontSize: 13, color: palette.muted, mb: 2 }}>
        Inviting a creator approves them straight away — they can start promoting
        your products as soon as they accept the notification.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or handle"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 18, color: palette.muted }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Niche"
          value={niche}
          onChange={(e) => setNiche(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All niches</MenuItem>
          {niches.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Followers"
          value={minFollowers}
          onChange={(e) => setMinFollowers(Number(e.target.value))}
          sx={{ minWidth: 130 }}
        >
          {FOLLOWER_TIERS.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${radius.sm}px` }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: palette.brand }} />
        </Box>
      ) : rows.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: 5, color: palette.muted }}>
          <PersonSearchOutlinedIcon sx={{ fontSize: 34, mb: 1 }} />
          <Typography sx={{ fontSize: 14 }}>
            No creators match that search.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {rows.map((row) => {
            const state = inviteState(row);
            return (
              <Stack
                key={row.handler_id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  p: 1.5,
                  border: `1px solid ${palette.line}`,
                  borderRadius: `${radius.md}px`,
                  transition: `border-color ${motion.base}`,
                  '&:hover': { borderColor: palette.brand },
                }}
              >
                <Avatar src={row.avatar_url || undefined} sx={{ width: 40, height: 40 }}>
                  {(row.display_name || '?').charAt(0).toUpperCase()}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: palette.ink }}>
                    {row.display_name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: palette.muted }}>
                    {row.username ? `@${row.username}` : ''}
                    {row.followers_count
                      ? `${row.username ? ' · ' : ''}${compactCount(row.followers_count)} followers`
                      : ''}
                  </Typography>
                  {row.niches?.length > 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      {row.niches.map((n) => (
                        <Chip key={n} label={n} size="small" sx={{ height: 20, fontSize: 10.5 }} />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Button
                  size="small"
                  variant={state.done ? 'text' : 'contained'}
                  disabled={state.disabled || inviting === row.handler_id}
                  startIcon={state.done ? <CheckCircleOutlineIcon /> : null}
                  onClick={() => invite(row)}
                  sx={{ textTransform: 'none', minWidth: 130, flexShrink: 0 }}
                >
                  {inviting === row.handler_id ? 'Inviting…' : state.label}
                </Button>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CreatorDirectory;
